import { useState } from 'react';
import { Button, Card, Col, Form, Row, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import SourceLeadsTable from './SourceLeadsTable';
import SourceLeadsHelp from './SourceLeadsHelp';
import useSourceLeads from './model/useSourceLeads';
import { SETTINGS_DIR_NAME_MAX_LENGTH } from '../types';
import EToolTip from 'components/others/EToolTip';
import SolarIcon from 'components/icons/SolarIcon';


const SourceLeads = () => {
    const { t } = useTranslation(['Settings', 'common']);
    const [activeTab, setActiveTab] = useState<'sources' | 'help'>('sources');
    const [isDomainsFieldVisible, setIsDomainsFieldVisible] = useState(false);
    const sanitizeDomainsInput = (value: string): string => value.replace(/[А-Яа-яЁёЇїІіЄєҐґ]/g, '');
    const {
        rows,
        isLoading,
        isCreating,
        newRow,
        setNewRow,
        editingId,
        setEditingId,
        editingRow,
        setEditingRow,
        createSourceLead,
        beginEdit,
        saveEdit,
        deleteRow,
    } = useSourceLeads();

    return (
        <Row>
            <Col sm={12}>
                <div className="w-100 d-flex pt-1 justify-content-between align-items-start mb-3 mb-md-2">
                    <div className="pb-1">
                        <h4 className="p-0 m-0">{t('sourceLeads.title')}</h4>
                        <small className="text-muted">{t('sourceLeads.subtitle')}</small>
                    </div>
                </div>
                <Card>
                    <Card.Body>
                        <Tabs
                            activeKey={activeTab}
                            onSelect={(eventKey) => setActiveTab((eventKey || 'sources') as 'sources' | 'help')}
                            variant="underline"
                            className="mb-3 invoice-tab border-bottom"
                        >
                            <Tab eventKey="sources" title={t('sourceLeads.tabs.sources')}>
                                <div className="pt-2">
                                    <Form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            createSourceLead();
                                        }}
                                        className="mb-3"
                                    >
                                        <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2">
                                            <Form.Control
                                                size="sm"
                                                value={newRow.sourceName}
                                                maxLength={SETTINGS_DIR_NAME_MAX_LENGTH}
                                                onChange={(e) => setNewRow((prev) => ({ ...prev, sourceName: e.target.value }))}
                                                placeholder={t('sourceLeads.placeholderName')}
                                                style={{ minWidth: '240px' }}
                                            />
                                            <EToolTip title={t('sourceLeads.allowedDomains')} placement="top">
                                                <Button
                                                    size="sm"
                                                    type="button"
                                                    variant={isDomainsFieldVisible ? 'outline-info' : 'outline-secondary'}
                                                    className="p-0 d-inline-flex align-items-center justify-content-center"
                                                    onClick={() => setIsDomainsFieldVisible((prev) => !prev)}
                                                    aria-label={t('sourceLeads.allowedDomains')}
                                                    style={{
                                                        width: '32px',
                                                        minWidth: '32px',
                                                        maxWidth: '32px',
                                                        height: '32px',
                                                        flex: '0 0 32px',
                                                    }}
                                                >
                                                    <SolarIcon name="globe2" />
                                                </Button>
                                            </EToolTip>
                                            {isDomainsFieldVisible ? (
                                                <Form.Control
                                                    size="sm"
                                                    value={newRow.sourceAllowDomain}
                                                    onChange={(e) =>
                                                        setNewRow((prev) => ({
                                                            ...prev,
                                                            sourceAllowDomain: sanitizeDomainsInput(e.target.value),
                                                        }))
                                                    }
                                                    placeholder={t('sourceLeads.placeholderAllowedDomains')}
                                                    style={{ minWidth: '280px' }}
                                                />
                                            ) : null}
                                            <Button
                                                size="sm"
                                                type="submit"
                                                variant="success"
                                                disabled={isCreating}
                                                className="px-2"
                                                style={{ height: '32px' }}
                                            >
                                                <SolarIcon name="plus" className="fs-14 me-1" />
                                                {isCreating ? t('common:buttons.creating') : t('common:buttons.create')}
                                            </Button>
                                        </div>
                                    </Form>

                                    <SourceLeadsTable
                                        isLoading={isLoading}
                                        rows={rows}
                                        editingId={editingId}
                                        editingRow={editingRow}
                                        setEditingRow={setEditingRow}
                                        onSaveEdit={saveEdit}
                                        onCancelEdit={() => setEditingId(null)}
                                        onBeginEdit={beginEdit}
                                        onDelete={deleteRow}
                                    />
                                </div>
                            </Tab>
                            <Tab eventKey="help" title={t('sourceLeads.tabs.help')}>
                                <div className="pt-2">
                                    <SourceLeadsHelp />
                                </div>
                            </Tab>
                        </Tabs>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

export default SourceLeads;
